import { lazy, Suspense, type JSX } from 'react';
import { Outlet, createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { AppShell, useLang } from './components/app-shell.js';
import { DiscoveryPage } from './pages/discovery-page.js';
import { ProviderLandingPage } from './pages/provider-landing-page.js';

// Lazy-load /refer and /welcome to keep initial chunk ≤ 150 KB Brotli.
// DiscoveryPage and ProviderLandingPage are statically imported (they ARE the initial chunk).
const ReferralFormPage = lazy(() => import('./pages/referral-form-page.js'));
const WelcomePage = lazy(() => import('./pages/welcome-page.js'));

/**
 * DiscoveryPageConnected — reads lang from AppShell context and passes to DiscoveryPage.
 */
function DiscoveryPageConnected(): JSX.Element {
  const lang = useLang();
  return <DiscoveryPage lang={lang} />;
}

/**
 * Access-Landing app router — four public routes, no auth guard.
 *
 * Routes:
 *   /          — CMS unified discovery (member-facing landing)
 *   /provider  — Provider landing page
 *   /refer     — Referral form (lazy chunk — Epic 2.1a scope)
 *   /welcome   — Member outreach landing (lazy chunk — token-driven)
 *
 * No RequireAuth, no PortalAuthProvider, no Cognito session required on any route.
 * AppShell (with LanguageToggle) is applied via a single layout route.
 * Lang state flows from AppShell → children via LangContext.
 */
const router = createBrowserRouter([
  {
    element: (
      <AppShell>
        <Outlet />
      </AppShell>
    ),
    children: [
      { path: '/', element: <DiscoveryPageConnected /> },
      {
        path: '/refer',
        element: (
          <Suspense fallback={null}>
            <ReferralFormPage />
          </Suspense>
        ),
      },
      {
        path: '/welcome',
        element: (
          <Suspense fallback={null}>
            <WelcomePage />
          </Suspense>
        ),
      },
    ],
  },
  // Provider landing has its own full chrome (topbar + section navbar)
  { path: '/provider', element: <ProviderLandingPage /> },
]);

export function App(): JSX.Element {
  return <RouterProvider router={router} />;
}
